import { parse as parseDate } from 'date-fns/parse';
import { format as jformat } from 'date-fns-jalali/format';
import { differenceInDays } from 'date-fns-jalali/differenceInDays';
import { Col, Divider, Image, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppContext } from '@/context';
import { ForgetCardHttp } from '@/services';
import { AppModal, Flex, LoadingSpin } from '../common';
import { useConsumedStatusFn } from '@/hooks';
import QRCodeLoadAction from './QrCodeLoadAction';
import { envConfig } from '@/configs';

export enum ReserveMode {
  All = 1,
  Self = 2,
  Teammate = 3
};

export interface ReserveInfoModalData {
  meal: ReserveMealEntity;
  report: ReportDayEntity;
  mealTypes: WeekdayMealTypeEntity[];
  mode?: ReserveMode;
  startServeTime?: any;
  endServeTime?: any;
  demonstrationDescription?: string;
}

export interface ReserveInfoModalProps {
  variant: ComponentVariant;
  open?: boolean;
  hide: () => void;
  data: ReserveInfoModalData;
}

export function ReserveInfoModal({
  hide,
  open = false,
  data,
  variant
}: ReserveInfoModalProps) {


  const [t] = useTranslation(['common', 'reserve']);
  const { mode: appMode } = useAppContext();
  const { report = {} as any, meal = {} as any, mode = ReserveMode.All } = (data ?? {});
  const [actions, setActions] = useState([]);

  const getConsumeStatus = useConsumedStatusFn();

  const reserves = useMemo(() => {
    const { mealTypes = [], mode = ReserveMode.All, startServeTime, endServeTime, demonstrationDescription } = (data ?? {});
    const selfMealTypes: WeekdayMealTypeEntity[] = [];
    const teammateMealTypes: WeekdayMealTypeEntity[] = [];
    for (const mealType of mealTypes) {
      if (mealType?.reserve) {
        if ([ReserveMode.All, ReserveMode.Self].includes(mode)) {
          selfMealTypes.push(mealType);
        }
      }
      if (mealType?.teammateReserve) {
        if ([ReserveMode.All, ReserveMode.Teammate].includes(mode)) {
          teammateMealTypes.push(mealType);
        }
      }
    }
    const mergedMealTypes: WeekdayMealTypeEntity[] = [...selfMealTypes, ...teammateMealTypes];
    return { mealTypes: mergedMealTypes, selfMealTypes, teammateMealTypes, startServeTime, endServeTime, demonstrationDescription };
  }, [data]);



  const { daysDiff, dayTitle } = useMemo(() => {
    const firstReport = report?.[0] ?? {};
    const dateStr = firstReport.date ?? firstReport.programDate ?? report?.date;
    if (!dateStr) return { date: '', dateFormatted: '', daysDiff: 0 };
    const date = parseDate(dateStr, 'yyyy-MM-dd', new Date());
    const dayTitle = jformat(date, 'dd LLLL');
    return { date, dayTitle, daysDiff: differenceInDays(date, new Date()) };
  }, [report]);

  useEffect(() => {
    if (!open) return;
    const { mode = ReserveMode.All } = (data ?? {});
    if ([ReserveMode.All, ReserveMode.Self].includes(mode)) {
      const asyncActions = [];
      for (const item of reserves?.selfMealTypes) {
        asyncActions.push(() => ForgetCardHttp.getQRCode(item?.reserve?.id, item?.reserve?.selectedCount || 0, false));
      }
      setActions(asyncActions);
    }
  }, [data?.mealTypes]);

  const renderRow = useCallback((reserve: ReserveReportItem, key: string | number, isTeammate: boolean) => {
    // const matchingDailySales = report?.dailySales?.filter((sale: DailySaleProgramEntity) =>
    //   sale.mealTypeId === reserve.mealTypeId
    // ) || [];

    return (
      <div key={String(key) + meal?.id} className="flex bg-[#f2f2f2] p-3 rounded">
        <Row className="w-full">
          <Col lg={14} span={24}>
            <Flex direction="column">
              {(!isTeammate || !reserve?.username) ? '' : (
                <Flex
                  gap={2}
                  inlineFlex
                  align="center"
                  direction="row"
                  className="bg-app-orange/85 text-[#444] font-medium py-1 px-2 rounded-sm me-auto">
                  <span>{reserve?.firstName} {reserve?.lastName}</span>
                  <span className="text-xs text-[#555]">
                    ({t('common:labels.teammate-name')})
                  </span>
                </Flex>
              )}
              <div className="text-base font-bold">{meal?.name}</div>
              <div className="font-bold !text-black">{reserve?.foodNames}</div>
              <div>
                {reserve?.selfCodeName}
              </div>
              <div>
                {t('common:labels.count')}: {reserve?.selectedCount} {t('common:labels.no-')}
              </div>
              {appMode === 'nurture' &&
                <div className="mb-2">{t('common:labels.status')}: {getConsumeStatus(reserve?.consumed)}</div>
              }
              {
                (reserves.startServeTime?.timeStr && envConfig?.ui?.showLessReserveDetail === false) &&
                <div> <span>زمان شروع:  </span> {reserves.startServeTime.timeStr}</div>
              }
              {
                (reserves.endServeTime?.timeStr && envConfig?.ui?.showLessReserveDetail === false) &&
                <div> <span>زمان پایان:  </span> {reserves.endServeTime.timeStr}</div>
              }
              {
                reserves?.demonstrationDescription &&
                <div> <span>توضیحات:  </span> {reserves?.demonstrationDescription}</div>
              }

            </Flex>
          </Col>
          <Col lg={10} span={24}>
            <Flex
              justify="center"
              className="h-full w-full flex-col xs:flex-row xs:items-start"
            >
              {reserve?.foodImageSrc && (
                <Image
                  width={120}
                  height={120}
                  preview={false}
                  style={{ minWidth: 120 }}
                  src={reserve?.foodImageSrc}
                  className="aspect-square object-cover object-center rounded-lg shadow-sm"
                />
              )}
              {(isTeammate || daysDiff < 0) ? '' : (actions?.[key]) ? <QRCodeLoadAction action={actions?.[key]} /> :
                <Flex className="h-full w-full" justify="center" align="center">
                  <LoadingSpin className="m-auto" text="" />
                </Flex>
              }
            </Flex>
          </Col>
        </Row>
      </div>
    );
  }, [meal, actions, daysDiff, report?.dailySales]);

  return (
    <AppModal
      destroyOnClose
      centered
      width="90%"
      open={open}
      footer={null}
      onCancel={hide}
      variant={variant}
      style={{ maxWidth: '700px' }}
      title={`${t('reserve:labels.reserve-details')} - ${dayTitle}`}
    >
      <Flex direction="column">
        {mode === ReserveMode.Self && (
          <Flex direction="column" gap={2}>
            {reserves?.mealTypes?.map((mealType, key) => (
              renderRow(mealType.reserve, key, false)
            ))}
            {/* Daily Sales Section */}
            {report?.dailySales?.filter((sale: DailySaleProgramEntity) =>
              sale.mealTypeId === meal?.id
            ).map((sale: DailySaleProgramEntity, index: number) => (
              <div key={`daily-sale-${index}`} className="flex bg-[#f2f2f2] p-3 rounded">
                <Row className="w-full">
                  <Col lg={14} span={24}>
                    <Flex direction="column">
                      <div className="text-base font-bold">{sale.mealTypeName}</div>
                      <div className="font-bold !text-black">{sale.foodNames}</div>
                      <div>
                        {sale.selfName}
                      </div>
                      <div>
                        {t('common:labels.count')}: {sale.remainCount}
                      </div>
                      {
                        (sale.startServeTime?.timeStr && envConfig?.ui?.showLessReserveDetail === false) &&
                        <div> <span>زمان شروع:  </span> {sale.startServeTime.timeStr}</div>
                      }
                      {
                        (sale.endServeTime?.timeStr && envConfig?.ui?.showLessReserveDetail === false) &&
                        <div> <span>زمان پایان:  </span> {sale.endServeTime.timeStr}</div>
                      }
                      {
                        sale.demonstrationDescription &&
                        <div> <span>توضیحات:  </span> {sale.demonstrationDescription}</div>
                      }
                    </Flex>
                  </Col>
                  <Col lg={10} span={24}>
                    <Flex
                      justify="center"
                      className="h-full w-full flex-col xs:flex-row xs:items-start"
                    >
                      {sale.foodImageSrc && (
                        <Image
                          width={120}
                          height={120}
                          preview={false}
                          style={{ minWidth: 120 }}
                          src={sale.foodImageSrc}
                          className="aspect-square object-cover object-center rounded-lg shadow-sm"
                        />
                      )}
                    </Flex>
                  </Col>
                </Row>
              </div>
            ))}
          </Flex>
        )}

        {mode === ReserveMode.Teammate && reserves?.mealTypes?.map((mealType, key) => (
          renderRow(mealType.teammateReserve, key, true)
        ))}

        {mode === ReserveMode.All && (
          <Flex direction="column">
            {/* Self Reserves */}
            {reserves?.selfMealTypes.length > 0 &&
              <>
                <h3 className="!my-0 !mb-2">{t('common:labels.your-reserves')}</h3>
                {reserves?.selfMealTypes?.map((mealType, key) => renderRow(mealType.reserve, key, false))}
              </>
            }

            {/* Daily Sales Section */}
            {report?.dailySales?.filter((sale: DailySaleProgramEntity) =>
              sale.mealTypeId === meal?.id
            ).length > 0 && (
                <>
                  <h3 className="!my-0 !mb-2">{t('reserve:labels.daily-sale')}</h3>
                  {report?.dailySales?.filter((sale: DailySaleProgramEntity) =>
                    sale.mealTypeId === meal?.id
                  ).map((sale: DailySaleProgramEntity, index: number) => (
                    <div key={`daily-sale-${index}`} className="flex bg-[#f2f2f2] p-3 rounded">
                      <Row className="w-full">
                        <Col lg={14} span={24}>
                          <Flex direction="column">
                            <div className="text-base font-bold">{sale.mealTypeName}</div>
                            <div className="font-bold !text-black">{sale.foodNames}</div>
                            <div>
                              {sale.selfName}
                            </div>
                            <div>
                              {t('common:labels.count')}: {sale.remainCount}
                            </div>
                            {
                              (sale.startServeTime?.timeStr && envConfig?.ui?.showLessReserveDetail === false) &&
                              <div> <span>زمان شروع:  </span> {sale.startServeTime.timeStr}</div>
                            }
                            {
                              (sale.endServeTime?.timeStr && envConfig?.ui?.showLessReserveDetail === false) &&
                              <div> <span>زمان پایان:  </span> {sale.endServeTime.timeStr}</div>
                            }
                            {
                              sale.demonstrationDescription &&
                              <div> <span>توضیحات:  </span> {sale.demonstrationDescription}</div>
                            }
                          </Flex>
                        </Col>
                        <Col lg={10} span={24}>
                          <Flex
                            justify="center"
                            className="h-full w-full flex-col xs:flex-row xs:items-start"
                          >
                            {sale.foodImageSrc && (
                              <Image
                                width={120}
                                height={120}
                                preview={false}
                                style={{ minWidth: 120 }}
                                src={sale.foodImageSrc}
                                className="aspect-square object-cover object-center rounded-lg shadow-sm"
                              />
                            )}
                          </Flex>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </>
              )}

            {/* Divider */}
            {
              reserves?.selfMealTypes.length > 0 &&
              reserves?.teammateMealTypes.length > 0 &&
              <Divider className="!my-2" />
            }

            {/* Teammates Reserves */}
            {reserves?.teammateMealTypes.length > 0 && (
              <>
                <h3 className="!my-0 !mb-2">{t('common:labels.teammate-reserves')}</h3>
                {reserves?.teammateMealTypes?.map((mealType, key) => renderRow(mealType.teammateReserve, key, true))}
              </>
            )}
          </Flex>
        )}
      </Flex>
    </AppModal>
  );
};

export default ReserveInfoModal;
